import { Component, inject, OnInit, signal, ChangeDetectionStrategy } from "@angular/core";
import { FaultListService } from "../../../../services/api/fault-list/fault-list.service";
import { createTimeoutSignal } from "../../../../shared/utils/timeout.utils";
import { FaultListLanguage } from "../../../../models/api/fault/faultListLanguage";
import { SvgIconComponent } from "../../../../shared/components/svg-icon/svg-icon.component";
import { ButtonComponent } from "../../../../shared/components/button/button.component";
import { DialogService, DynamicDialogRef } from "@openng/optimus-ui/dynamicdialog";
import { LanguageComponent } from "../forms/language/language.component";
import { HttpErrorResponse } from "@angular/common/http";
import { MessageService } from "@openng/optimus-ui/api";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { ProgressSpinnerModule } from "@openng/optimus-ui/progressspinner";
import { TimeoutComponent } from "../../../../shared/components/timeout/timeout.component";

@Component({
  selector: "app-language-management",
  standalone: true,
  imports: [SvgIconComponent, ButtonComponent, ProgressSpinnerModule, TimeoutComponent, TranslateModule],
  templateUrl: "./language-management.component.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: "./language-management.component.scss",
})
export class LanguageManagementComponent implements OnInit {
  private faultListService = inject(FaultListService);
  private dialog = inject(DialogService);
  private message = inject(MessageService);
  private translate = inject(TranslateService);

  private languageRef: DynamicDialogRef | null = null;

  public loadingLanguages = signal(true);
  public timeoutLanguages = createTimeoutSignal(this.loadingLanguages);

  public languages = signal<FaultListLanguage[] | null>([]);

  public ngOnInit() {
    this.getLanguages();
  }

  /**
   * Loads the list of languages from the backend.
   * Called on component initialization.
   */
  private getLanguages() {
    this.loadingLanguages.set(true);
    this.faultListService.getFaultListLanguage().subscribe({
      next: (langs) => {
        this.languages.set(langs);
        this.loadingLanguages.set(false);
      },
      error: () => {
        this.languages.set(null);
        this.loadingLanguages.set(false);
      },
    });
  }

  /**
   * Deletes a language from the backend.
   * If the deletion is successful, it refreshes the list of languages.
   * If the deletion fails, it displays an error message.
   * @param languageCode The code of the language to delete.
   */
  public deleteLanguage(languageCode: string | undefined) {
    if (languageCode) {
      this.faultListService.deleteFaultListLanguage(languageCode).subscribe({
        next: () => {
          this.getLanguages();
        },
        error: (err: HttpErrorResponse) => {
          this.message.add({
            key: "global",
            severity: "error",
            summary: this.translate.instant("common.error"),
            detail: this.translate.instant("error_messages." + err.error.code),
            life: 4500,
          });
        },
      });
    }
  }

  /**
   * Subscribes to onClose event of language form.
   * Used to refresh the list of languages after a language was edited or created.
   */
  private onCloseLangForm() {
    this.languageRef?.onClose.subscribe((res) => {
      if (res === "refresh") {
        this.getLanguages();
      }
    });
  }

  /**
   * Opens the language form component in edit mode.
   * The user can edit the given language and save the changes.
   * If the save is successful, the language list will be refreshed.
   * @param language The language to be edited.
   */
  public editLanguage(language: FaultListLanguage) {
    this.languageRef = this.dialog.open(LanguageComponent, {
      header: this.translate.instant("fault_allocation.fault_lists.language_management.edit_language"),
      modal: true,
      contentStyle: { width: "45vw", "max-width": "750px", overflow: "auto" },
      data: {
        actionType: "edit",
        language,
      },
      closable: true,
    });
    this.onCloseLangForm();
  }

  /**
   * Opens the language form component in add mode.
   * The user can enter data for a new language and save it.
   * If the save is successful, the language list will be refreshed.
   */
  public addLanguage() {
    const emptyLanguage: FaultListLanguage = {
      languageCode: "",
      languageName: "",
      languageData: "",
    };
    this.languageRef = this.dialog.open(LanguageComponent, {
      header: "Sprache hinzufügen",
      modal: true,
      contentStyle: { width: "45vw", "max-width": "750px", overflow: "auto" },
      data: {
        actionType: "add",
        language: emptyLanguage,
      },
      closable: true,
    });
    this.onCloseLangForm();
  }
}
